import React, { useState, useEffect } from 'react';
import EscaloneLoading from '../EscaloneLoading';
import { useLocation, useNavigate } from 'react-router-dom';
import type { LoginCredentials, User, Empresa, UserRole } from '../../types';
import { ArrowLeft, Eye, EyeOff, Mail, Lock, Building } from 'lucide-react';

interface LoginProps {
  onLogin: (credentials: LoginCredentials) => void;
  onSelectEmpresa: (user: User, empresaId?: string) => void;
  empresas: Empresa[];
  onSignUpClick: () => void;
  tempUser?: User | null;
  step?: 'credentials' | 'empresa';
  loadingEmpresas?: boolean;
}

const Login: React.FC<LoginProps> = ({ 
  onLogin, 
  onSelectEmpresa, 
  empresas, 
  onSignUpClick, 
  tempUser: propTempUser, 
  step: propStep,
  loadingEmpresas = false 
}) => {
  const navigate = useNavigate();  const [step, setStep] = useState<'credentials' | 'empresa'>(propStep || 'credentials');
  const [credentials, setCredentials] = useState({ email: '', senha: '', empresaId: '' });
  const [tempUser, setTempUser] = useState<User | null>(propTempUser || null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [empresasFiltradas, setEmpresasFiltradas] = useState<Empresa[]>([]);
  const adminEmails = ['admin@refricril.com', 'admin@refricril.com.br'];
  const isGlobalAdmin = !!(propTempUser || tempUser) &&
    ((propTempUser || tempUser) as User).role === 'administrator' &&
    adminEmails.includes((((propTempUser || tempUser) as User).email || '').toLowerCase());
  const [showPassword, setShowPassword] = useState(false);
  const location = useLocation();

  useEffect(() => {
    // Verificar se há dados do convite no state
    const state = location.state as any;
    if (state?.email) {
      setCredentials(prev => ({ ...prev, email: state.email }));
      setMessage(state.message || '');
      
      // Se tiver empresa, selecionar automaticamente
      if (state.empresa) {
        const empresa = empresas.find(e => e.nome === state.empresa);
        if (empresa) {
          setCredentials(prev => ({ ...prev, empresaId: empresa.id }));
        }
      }
    }
  }, [location, empresas]);

  // Atualiza a lista de empresas filtradas quando o usuário mudar
  useEffect(() => {
    if (tempUser) {
      let filtradas = empresas;
      
      // Se não for ADMIN GLOBAL, filtra as empresas
      if (!isGlobalAdmin) {
        filtradas = empresas.filter(empresa => {
          // Verifica email
          if (empresa.email === tempUser.email) return true;
          
          // Verifica empresaId
          if (tempUser.empresaId === empresa.id) return true;
          
          // Verifica empresasPermitidas
          if (tempUser.empresasPermitidas && 
              Array.isArray(tempUser.empresasPermitidas) && 
              tempUser.empresasPermitidas.includes(empresa.id)) {
            return true;
          }
          
          return false;
        });
      }
      
  setEmpresasFiltradas(filtradas);
      
      // Log para depuração
      console.log("Empresas filtradas para", tempUser.nome, ":", filtradas);
    }
  }, [tempUser, empresas]);

  // Sincronizar com props vindas do App
  useEffect(() => {
    if (propTempUser !== undefined) {
      setTempUser(propTempUser);
    }
    if (propStep !== undefined) {
      setStep(propStep);
    }
  }, [propTempUser, propStep]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
  
    try {
      // A senha é conferida no SERVIDOR (/api/login), que guarda só o hash.
      // Antes a tela buscava /api/users (que devolvia a senha) e comparava aqui:
      // qualquer um que chamasse essa rota lia a senha de qualquer pessoa.
      const resp = await fetch('/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: credentials.email.trim(), senha: credentials.senha }),
      });
      const resultado = await resp.json().catch(() => ({}));
      if (!resp.ok || !resultado.success || !resultado.user) {
        throw new Error(resultado.error || 'Erro ao fazer login');
      }

      const user = resultado.user;
      if (user.status && user.status !== 'ativo') {
        throw new Error('Usuário pendente de verificação');
      }

      const mappedUser = {
        ...user,
        role: (user.role || 'viewer') as UserRole,
        id: user._id || user.id,
        createdAt: user.createdAt ? new Date(user.createdAt) : new Date(),
        updatedAt: user.updatedAt ? new Date(user.updatedAt) : new Date()
      };
      setTempUser(mappedUser);

      // Sempre chamar onLogin para processar autenticação completa e carregar empresas
      onLogin(credentials);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Erro ao fazer login');
    }
  };  const handleSelectEmpresa = (empresaId: string) => {
    if (tempUser) {
      onSelectEmpresa(tempUser, empresaId);
    }
  };

  if (step === 'empresa' && tempUser) {
    // Log para depuração
    console.log("Renderizando tela de seleção de empresa para:", tempUser.nome);
    console.log("Role:", tempUser.role);
    console.log("Empresas disponíveis:", empresasFiltradas);
    console.log("Loading empresas:", loadingEmpresas);
    
    return (
      <div className="min-h-screen bg-gray-50/50 flex flex-col items-center justify-center px-4">
        {/* Loading Overlay */}
        {loadingEmpresas && (
          <div className="fixed inset-0 z-50">
            <EscaloneLoading />
          </div>
        )}

        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-14 h-14 mx-auto rounded-xl bg-indigo-600 text-white flex items-center justify-center font-bold text-xl mb-4">E</div>
            <h1 className="text-lg font-bold text-gray-600 mb-0.5">Escalone</h1>
            <p className="text-xs text-gray-400">by Refricril</p>
          </div>

          <div className="bg-white p-8 rounded-xl border border-gray-200 shadow-sm">
            <h2 className="text-xl font-bold text-gray-900 text-center mb-2">
              Selecione uma Empresa
            </h2>
            <p className="text-sm text-gray-500 text-center mb-6">
              Olá <span className="font-semibold text-indigo-600">{tempUser.nome}</span>, selecione a empresa que deseja acessar:
            </p>

            <div className="space-y-3">
              {/* Botão: apenas admin global pode entrar sem selecionar empresa */}
              {tempUser.role === 'administrator' && ['admin@refricril.com', 'admin@refricril.com.br'].includes((tempUser.email || '').toLowerCase()) && (
                <button
                  onClick={() => onSelectEmpresa(tempUser)}
                  className="w-full p-4 text-left rounded-lg bg-gray-900 text-white hover:bg-gray-800 transition-colors disabled:opacity-50"
                  disabled={loadingEmpresas}
                >
                  <div className="font-semibold text-sm">Entrar sem selecionar empresa</div>
                  <div className="text-xs text-gray-300">Acesso a todas as empresas</div>
                </button>
              )}

              {/* Lista de empresas filtrada */}
              {empresasFiltradas.length === 0 && !loadingEmpresas ? (
                <div className="p-6 text-center text-gray-500 border border-dashed border-gray-300 rounded-lg bg-gray-50/50">
                  <Building className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                  <p className="font-medium text-sm mb-1">Nenhuma empresa disponível</p>
                  <p className="text-xs">Entre em contato com o administrador.</p>
                </div>
              ) : (
                empresasFiltradas.map((empresa) => (
                  <button
                    key={empresa.id}
                    onClick={() => handleSelectEmpresa(empresa.id)}
                    className="w-full p-3 text-left border border-gray-200 rounded-lg hover:border-indigo-200 hover:bg-indigo-50/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={loadingEmpresas}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-indigo-100 text-indigo-600 flex items-center justify-center font-bold text-sm">
                        {empresa.nome.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="font-semibold text-sm text-gray-900">{empresa.nome}</div>
                        <div className="text-xs text-gray-500">{empresa.razaoSocial}</div>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>

            <button
              onClick={() => setStep('credentials')}
              className="w-full mt-5 text-sm text-gray-500 hover:text-gray-900 transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5 font-medium"
              disabled={loadingEmpresas}
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar para o login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50/50 flex flex-col items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-14 h-14 mx-auto rounded-xl bg-indigo-600 text-white flex items-center justify-center font-bold text-xl mb-4">E</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">Login</h1>
          <p className="text-sm text-gray-500">Entre na sua conta Escalone</p>
        </div>

        {message && (
          <div className="p-3 text-sm text-blue-700 bg-blue-50 rounded-lg text-center mb-5 border border-blue-200">
            {message}
          </div>
        )}

        <div className="bg-white p-8 rounded-xl border border-gray-200 shadow-sm">
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="p-3 text-sm text-red-700 bg-red-50 rounded-lg text-center border border-red-200">
                {error}
              </div>
            )}
            
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  id="email"
                  type="email"
                  required
                  value={credentials.email}
                  onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
                  className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 outline-none transition-colors"
                  placeholder="Digite seu email"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1.5">
                Senha
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  required
                  value={credentials.senha}
                  onChange={(e) => setCredentials({ ...credentials, senha: e.target.value })}
                  className="w-full pl-10 pr-12 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 outline-none transition-colors"
                  placeholder="Digite sua senha"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* Campo de empresa se vier do convite */}
            {credentials.empresaId && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Empresa
                </label>
                <div className="relative">
                  <Building className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <select
                    value={credentials.empresaId}
                    onChange={(e) => setCredentials({ ...credentials, empresaId: e.target.value })}
                    className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 outline-none transition-colors"
                  >
                    <option value="">Selecione uma empresa</option>
                    {empresas.map(empresa => (
                      <option key={empresa.id} value={empresa.id}>
                        {empresa.nome}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            <button
              type="submit"
              className="w-full py-2.5 px-4 rounded-lg text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 transition-colors"
            >
              Entrar
            </button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate('/reset-password', { state: { email: credentials.email } })}
                className="text-sm text-gray-500 hover:text-gray-900 transition-colors font-medium"
              >
                Esqueci minha senha
              </button>
            </div>
          </form>
        </div>

        <div className="text-center text-sm text-gray-500 mt-6">
          Não tem uma conta?{' '}
          <button
            onClick={onSignUpClick}
            className="text-indigo-600 hover:text-indigo-700 font-semibold hover:underline transition-colors"
          >
            Crie sua conta gratuita
          </button>
        </div>

        <div className="text-center text-sm mt-3">
          <button
            onClick={() => navigate('/')}
            className="text-indigo-600 hover:text-indigo-700 font-semibold hover:underline transition-colors"
          >
            Conheça o Escalone
          </button>
        </div>
      </div>
    </div>
  );
};

export default Login;